import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdEngine } from '../ads/AdEngine'
import { fetchVast, fireTrackingUrls } from '../ads/VastClient'
import { fetchVmap } from '../ads/VmapClient'
import { EventBus } from '../events/EventBus'
import { PlayerCore } from '../PlayerCore'
import { BuiltInViews } from '../stats/BuiltInViews'
import { ResumeStore } from '../storage/ResumeStore'
import type {
  AdBreak,
  AdCreative,
  CaptionStyle,
  PlayerConfig,
  PlayerEventName,
  PlayerUiState,
  SettingsPanel,
} from '../types'
import { resolveInitialStartTime } from '../utils/startTime'

function videoFingerprint(config: PlayerConfig): string | undefined {
  return (
    config.content.hlsUrl ||
    config.content.mp4Qualities?.find((q) => q.src)?.src ||
    config.content.title ||
    undefined
  )
}

function resolveViewsKey(config: PlayerConfig): string | null {
  const stats = config.stats
  if (!stats) return null
  if (stats.trackViewUrl || stats.viewsUrl) return null
  if (stats.builtIn === false && !stats.viewsKey) return null
  return BuiltInViews.resolveKey(stats.viewsKey, videoFingerprint(config))
}

const defaultCaptionStyle: CaptionStyle = {
  fontSize: 'medium',
  color: '#ffffff',
  backgroundOpacity: 0.75,
}

const initialUi = (config: PlayerConfig): PlayerUiState => {
  const key = resolveViewsKey(config)
  const cached = key ? BuiltInViews.peek(key) : null
  return {
    playing: false,
    currentTime: 0,
    duration: 0,
    buffered: 0,
    volume: 1,
    muted: !!config.muted,
    playbackRate: 1,
    fullscreen: false,
    pip: false,
    theater: !!config.theater,
    miniPlayer: false,
    buffering: false,
    seeking: false,
    controlsVisible: true,
    settingsPanel: null,
    infoOpen: false,
    hotkeysOpen: false,
    views:
      cached ??
      (typeof config.stats?.views === 'number' ? config.stats.views : key ? 0 : null),
    videoTitle: config.content.title ?? '',
    videoDescription: config.stats?.description ?? '',
    qualityLevels: config.content.mp4Qualities ?? [],
    activeQualityId: 'auto',
    qualityLabel: 'Auto',
    captionsOn: false,
    captionTracks: config.content.captions ?? [],
    captionStyle: defaultCaptionStyle,
    activeCaptionId: config.content.captions?.find((c) => c.default)?.src ?? null,
    audioTracks: config.audioTracks ?? [],
    activeAudioId: config.audioTracks?.[0]?.id ?? null,
    isAdPlaying: false,
    adTitle: '',
    adCurrentTime: 0,
    adDuration: 0,
    adSkipRemaining: 0,
    adCanSkip: false,
    adMarkers: [],
    chapters: config.chapters ?? [],
    hasStarted: false,
    ended: false,
    error: null,
    resumeOffer: null,
    isLive: !!config.live,
    liveLatency: 0,
    brightness: 1,
    seekFlash: null,
    watchTime: 0,
    sleepRemainingSec: null,
    sleepLabel: null,
    shareToast: null,
    offlineStatus: 'idle',
    offlineProgress: 0,
    castAvailable: false,
    casting: false,
  }
}

export function usePlayer(config: PlayerConfig) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const coreRef = useRef<PlayerCore | null>(null)
  const adsRef = useRef(new AdEngine(config.adBreaks ?? []))
  const busRef = useRef(new EventBus())
  const contentTimeRef = useRef(0)
  const isAdRef = useRef(false)
  const skipTimerRef = useRef<number | null>(null)
  const midrollLockRef = useRef(false)
  const hasStartedRef = useRef(false)
  const adCanSkipRef = useRef(false)
  const viewTrackedRef = useRef(false)
  const activeCreativeRef = useRef<AdCreative | null>(null)
  const adQuartilesRef = useRef(new Set<string>())
  const resumeKeyRef = useRef('default')
  const lastSaveRef = useRef(0)
  const initialSeekDone = useRef(false)
  const configRef = useRef(config)
  configRef.current = config

  const [ui, setUi] = useState<PlayerUiState>(() => initialUi(config))

  const patch = useCallback((partial: Partial<PlayerUiState>) => {
    setUi((s) => ({ ...s, ...partial }))
  }, [])

  const clearSkipTimer = useCallback(() => {
    if (skipTimerRef.current) {
      window.clearInterval(skipTimerRef.current)
      skipTimerRef.current = null
    }
  }, [])

  const startSkipCountdown = useCallback(
    (skipAfter: number) => {
      clearSkipTimer()
      const start = performance.now()
      adCanSkipRef.current = skipAfter <= 0
      patch({ adSkipRemaining: skipAfter, adCanSkip: skipAfter <= 0 })
      skipTimerRef.current = window.setInterval(() => {
        const elapsed = (performance.now() - start) / 1000
        const remaining = Math.max(0, Math.ceil(skipAfter - elapsed))
        const can = remaining <= 0
        adCanSkipRef.current = can
        patch({ adSkipRemaining: remaining, adCanSkip: can })
        if (can) clearSkipTimer()
      }, 200)
    },
    [clearSkipTimer, patch],
  )

  const finishAdAndResume = useCallback(
    async (resumeTime: number) => {
      const core = coreRef.current
      if (!core) return
      clearSkipTimer()
      const creative = activeCreativeRef.current
      fireTrackingUrls(creative?.tracking?.complete)
      activeCreativeRef.current = null
      isAdRef.current = false
      adCanSkipRef.current = false
      busRef.current.emit('adend')
      patch({
        isAdPlaying: false,
        adTitle: '',
        adCurrentTime: 0,
        adDuration: 0,
        adSkipRemaining: 0,
        adCanSkip: false,
      })
      await core.resumeContent(resumeTime, true)
      window.setTimeout(() => {
        midrollLockRef.current = false
      }, 1500)
    },
    [clearSkipTimer, patch],
  )

  const beginAdBreak = useCallback(
    async (breakItem: AdBreak) => {
      const core = coreRef.current
      if (!core) return
      const active = adsRef.current.startBreak(breakItem, contentTimeRef.current)
      if (!active) return
      isAdRef.current = true
      midrollLockRef.current = true
      hasStartedRef.current = true
      activeCreativeRef.current = active.creative
      adQuartilesRef.current = new Set()
      fireTrackingUrls(active.creative.tracking?.impression)
      fireTrackingUrls(active.creative.tracking?.start)
      busRef.current.emit('adstart', { id: active.creative.id })
      patch({
        isAdPlaying: true,
        adTitle: active.creative.title ?? 'Advertisement',
        hasStarted: true,
        ended: false,
        settingsPanel: null,
        error: null,
      })
      startSkipCountdown(active.creative.skipAfterSeconds)
      await core.loadAd(active.creative.src)
    },
    [patch, startSkipCountdown],
  )

  const handleAdFinished = useCallback(async () => {
    const result = adsRef.current.onAdEnded()
    const core = coreRef.current
    if (!core) return
    if (result.next) {
      activeCreativeRef.current = result.next.creative
      adQuartilesRef.current = new Set()
      fireTrackingUrls(result.next.creative.tracking?.impression)
      fireTrackingUrls(result.next.creative.tracking?.start)
      startSkipCountdown(result.next.creative.skipAfterSeconds)
      patch({ adTitle: result.next.creative.title ?? 'Advertisement' })
      await core.loadAd(result.next.creative.src)
      return
    }
    await finishAdAndResume(result.resumeContentTime)
  }, [finishAdAndResume, patch, startSkipCountdown])

  const beginAdBreakRef = useRef(beginAdBreak)
  const handleAdFinishedRef = useRef(handleAdFinished)
  beginAdBreakRef.current = beginAdBreak
  handleAdFinishedRef.current = handleAdFinished

  const trackViewOnce = useCallback(async () => {
    if (viewTrackedRef.current) return
    viewTrackedRef.current = true
    const stats = configRef.current.stats
    if (!stats) return
    if (stats.trackViewUrl) {
      setUi((s) => ({
        ...s,
        views: typeof s.views === 'number' ? s.views + 1 : (stats.views ?? 0) + 1,
      }))
      try {
        await fetch(stats.trackViewUrl, {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
        })
      } catch {
        /* ignore */
      }
      return
    }
    const key = resolveViewsKey(configRef.current)
    if (!key) {
      if (typeof stats.views === 'number') patch({ views: stats.views + 1 })
      return
    }
    patch({ views: await BuiltInViews.hit(key) })
  }, [patch])

  const markStarted = useCallback(() => {
    if (hasStartedRef.current) return
    hasStartedRef.current = true
    patch({ hasStarted: true })
    void trackViewOnce()
  }, [patch, trackViewOnce])

  // Resolve ad breaks (local + VAST/VMAP)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let breaks = [...(config.adBreaks ?? [])]
      try {
        if (config.ads?.vmapUrl) {
          const remote = await fetchVmap(config.ads.vmapUrl)
          if (!cancelled && remote.length) breaks = [...remote, ...breaks]
        } else if (config.ads?.vastTagUrl) {
          const vast = await fetchVast(config.ads.vastTagUrl)
          if (!cancelled && vast) breaks = [vast, ...breaks]
        }
      } catch (e) {
        if (!cancelled) {
          busRef.current.emit('error', { message: String(e) })
        }
      }
      if (cancelled) return
      adsRef.current.reset(breaks)
      patch({ adMarkers: adsRef.current.getMarkers() })
    })()
    return () => {
      cancelled = true
    }
  }, [config.adBreaks, config.ads?.vastTagUrl, config.ads?.vmapUrl, patch])

  // Views load
  useEffect(() => {
    let cancelled = false
    const stats = config.stats
    if (!stats) return
    ;(async () => {
      const key = resolveViewsKey(config)
      if (!key) return
      const value = await BuiltInViews.load(key)
      if (!cancelled) patch({ views: value })
    })()
    return () => {
      cancelled = true
    }
  }, [config, patch])

  // Core init
  useEffect(() => {
    const video = videoRef.current
    const container = containerRef.current
    if (!video || !container) return

    const fp = videoFingerprint(config) || 'default'
    resumeKeyRef.current = ResumeStore.key(config.resume?.key, fp)
    busRef.current.setHost(container)
    busRef.current.setEnabled(config.events !== false)
    busRef.current.setHeatmapKey(fp)
    busRef.current.resetMilestones()

    hasStartedRef.current = false
    viewTrackedRef.current = false
    contentTimeRef.current = 0
    isAdRef.current = false
    midrollLockRef.current = false
    initialSeekDone.current = false
    clearSkipTimer()

    setUi({
      ...initialUi(config),
      adMarkers: adsRef.current.getMarkers(),
    })

    if (config.muted) video.muted = true

    const core = new PlayerCore(video, container, {
      onTimeUpdate: (currentTime, duration, buffered) => {
        if (isAdRef.current) {
          patch({ adCurrentTime: currentTime, adDuration: duration })
          const creative = activeCreativeRef.current
          if (creative && duration > 0) {
            const pct = currentTime / duration
            const q = adQuartilesRef.current
            if (pct >= 0.25 && !q.has('q1')) {
              q.add('q1')
              fireTrackingUrls(creative.tracking?.firstQuartile)
            }
            if (pct >= 0.5 && !q.has('q2')) {
              q.add('q2')
              fireTrackingUrls(creative.tracking?.midpoint)
            }
            if (pct >= 0.75 && !q.has('q3')) {
              q.add('q3')
              fireTrackingUrls(creative.tracking?.thirdQuartile)
            }
          }
          return
        }
        contentTimeRef.current = currentTime
        busRef.current.noteTime(currentTime, duration, !video.paused)
        patch({
          currentTime,
          duration,
          buffered,
          watchTime: busRef.current.getWatchTime(),
        })

        if (
          !initialSeekDone.current &&
          duration > 0 &&
          Number.isFinite(duration)
        ) {
          initialSeekDone.current = true
          const urlStart = resolveInitialStartTime(config.startTime)
          if (urlStart > 0) {
            core.seek(Math.min(urlStart, duration - 1))
          } else if (config.resume?.enabled !== false) {
            const saved = ResumeStore.load(resumeKeyRef.current)
            const offer = ResumeStore.offer(saved, duration)
            if (offer != null) patch({ resumeOffer: offer })
          }
        }

        if (config.resume?.enabled !== false && duration > 0) {
          const now = Date.now()
          if (now - lastSaveRef.current > 5000) {
            lastSaveRef.current = now
            ResumeStore.save(resumeKeyRef.current, currentTime)
          }
        }

        if (!midrollLockRef.current && currentTime > 0.5) {
          const mid = adsRef.current.checkMidroll(currentTime)
          if (mid) void beginAdBreakRef.current(mid)
        }
      },
      onPlay: () => {
        busRef.current.notePlay()
        patch({ playing: true, ended: false, error: null })
      },
      onPause: () => {
        busRef.current.notePause()
        patch({ playing: false })
      },
      onEnded: () => {
        if (isAdRef.current) {
          void handleAdFinishedRef.current()
          return
        }
        ResumeStore.clear(resumeKeyRef.current)
        busRef.current.noteComplete()
        patch({ playing: false, ended: true })
      },
      onWaiting: () => patch({ buffering: true }),
      onPlaying: () => patch({ buffering: false }),
      onVolumeChange: (volume, muted) => patch({ volume, muted }),
      onRateChange: (playbackRate) => patch({ playbackRate }),
      onFullscreenChange: (fullscreen) => patch({ fullscreen }),
      onPipChange: (pip) => patch({ pip }),
      onQualityChange: (qualityLevels, activeQualityId, qualityLabel) => {
        patch({ qualityLevels, activeQualityId, qualityLabel })
        busRef.current.emit('quality', { activeQualityId, qualityLabel })
      },
      onError: (message) => {
        patch({ error: message, buffering: false })
        busRef.current.emit('error', { message })
      },
    })

    core.quality.setLiveHandler((isLive) => patch({ isLive }))
    coreRef.current = core

    const start = resolveInitialStartTime(config.startTime)
    void core.loadContent(config.content, 0, !!config.autoplay).then(() => {
      busRef.current.emit('ready')
      if (config.autoplay && start > 0) core.seek(start)
    })
    if (config.content.poster) video.poster = config.content.poster

    return () => {
      clearSkipTimer()
      core.dispose()
      coreRef.current = null
    }
  }, [config, clearSkipTimer, patch])

  const requestPlay = useCallback(async () => {
    const core = coreRef.current
    if (!core) return
    if (isAdRef.current) {
      await core.play()
      return
    }
    if (!hasStartedRef.current) {
      const preroll = adsRef.current.requestInitialPlay()
      if (preroll) {
        markStarted()
        await beginAdBreak(preroll)
        return
      }
      markStarted()
    }
    await core.play()
  }, [beginAdBreak, markStarted])

  const pause = useCallback(() => {
    coreRef.current?.pause()
  }, [])

  const togglePlay = useCallback(async () => {
    const core = coreRef.current
    if (!core) return
    if (core.video.paused) await requestPlay()
    else core.pause()
  }, [requestPlay])

  const seek = useCallback(
    (time: number) => {
      if (isAdRef.current) return
      coreRef.current?.seek(time)
      contentTimeRef.current = time
      patch({ currentTime: time, ended: false })
      busRef.current.emit('seek', { time })
    },
    [patch],
  )

  const seekBy = useCallback(
    (delta: number) => {
      if (isAdRef.current) return
      const t = Math.max(0, contentTimeRef.current + delta)
      seek(t)
      if (delta !== 0) {
        patch({
          seekFlash: { dir: delta < 0 ? 'left' : 'right', at: Date.now() },
        })
      }
    },
    [patch, seek],
  )

  const acceptResume = useCallback(() => {
    setUi((s) => {
      if (s.resumeOffer != null) {
        coreRef.current?.seek(s.resumeOffer)
        contentTimeRef.current = s.resumeOffer
      }
      return { ...s, resumeOffer: null }
    })
  }, [])

  const dismissResume = useCallback(() => {
    ResumeStore.clear(resumeKeyRef.current)
    patch({ resumeOffer: null })
  }, [patch])

  const retry = useCallback(async () => {
    const core = coreRef.current
    if (!core) return
    const t = contentTimeRef.current
    patch({ error: null, buffering: true })
    await core.resumeContent(t, true)
  }, [patch])

  const setVolume = useCallback((volume: number) => {
    coreRef.current?.setVolume(volume)
  }, [])

  const volumeBy = useCallback((delta: number) => {
    const core = coreRef.current
    if (!core) return
    core.setVolume(core.video.volume + delta)
  }, [])

  const toggleMute = useCallback(() => {
    coreRef.current?.toggleMute()
  }, [])

  const setPlaybackRate = useCallback((rate: number) => {
    if (isAdRef.current) return
    coreRef.current?.setPlaybackRate(rate)
  }, [])

  const setQuality = useCallback(async (id: string) => {
    if (isAdRef.current) return
    await coreRef.current?.quality.setQuality(id)
  }, [])

  const setAudioTrack = useCallback(
    async (id: string) => {
      if (isAdRef.current) return
      const track = configRef.current.audioTracks?.find((a) => a.id === id)
      const core = coreRef.current
      if (!core) return
      if (track?.src) {
        const time = core.video.currentTime
        const paused = core.video.paused
        await core.quality.loadPlainSrc(track.src, time, !paused)
        // reload as content replacement for muxed alt — keep time
        patch({ activeAudioId: id })
        return
      }
      const hlsTracks = core.quality.getHlsAudioTracks()
      const idx = hlsTracks.findIndex((t) => t.name === track?.label || String(t.id) === id)
      if (idx >= 0) {
        core.quality.setHlsAudioTrack(idx)
        patch({ activeAudioId: id })
      }
    },
    [patch],
  )

  const toggleFullscreen = useCallback(async () => {
    await coreRef.current?.toggleFullscreen()
  }, [])

  const togglePip = useCallback(async () => {
    if (isAdRef.current) return
    await coreRef.current?.togglePip()
  }, [])

  const toggleTheater = useCallback(() => {
    try {
      if (window.self !== window.top) return
    } catch {
      return
    }
    setUi((s) => {
      const theater = !s.theater
      busRef.current.emit('theater', { theater })
      const host = containerRef.current?.parentElement
      host?.classList.toggle('playerv--theater', theater)
      return { ...s, theater }
    })
  }, [])

  const setMiniPlayer = useCallback((miniPlayer: boolean) => {
    setUi((s) => {
      busRef.current.emit('miniplayer', { miniPlayer })
      return { ...s, miniPlayer }
    })
  }, [])

  const toggleCaptions = useCallback(() => {
    setUi((s) => {
      const next = !s.captionsOn
      coreRef.current?.setCaptions(next)
      return { ...s, captionsOn: next }
    })
  }, [])

  const setCaptionStyle = useCallback((captionStyle: CaptionStyle) => {
    patch({ captionStyle })
  }, [patch])

  const setSettingsPanel = useCallback((settingsPanel: SettingsPanel) => {
    patch({ settingsPanel, infoOpen: false, hotkeysOpen: false })
  }, [patch])

  const toggleInfo = useCallback(() => {
    setUi((s) => ({
      ...s,
      infoOpen: !s.infoOpen,
      settingsPanel: null,
      hotkeysOpen: false,
      controlsVisible: true,
    }))
  }, [])

  const toggleHotkeys = useCallback(() => {
    setUi((s) => ({
      ...s,
      hotkeysOpen: !s.hotkeysOpen,
      settingsPanel: null,
      infoOpen: false,
      controlsVisible: true,
    }))
  }, [])

  const setControlsVisible = useCallback((controlsVisible: boolean) => {
    patch({ controlsVisible })
  }, [patch])

  const setBrightness = useCallback((brightness: number) => {
    patch({ brightness: Math.max(0.4, Math.min(1.4, brightness)) })
  }, [patch])

  const sleepTimerRef = useRef<number | null>(null)
  const sleepEndModeRef = useRef(false)

  const clearSleep = useCallback(() => {
    if (sleepTimerRef.current) {
      window.clearInterval(sleepTimerRef.current)
      sleepTimerRef.current = null
    }
    sleepEndModeRef.current = false
    patch({ sleepRemainingSec: null, sleepLabel: null })
  }, [patch])

  const setSleepTimer = useCallback(
    (minutes: number | 'end' | null) => {
      clearSleep()
      if (minutes == null) return
      if (minutes === 'end') {
        sleepEndModeRef.current = true
        patch({ sleepRemainingSec: null, sleepLabel: 'End of video' })
        return
      }
      const total = Math.max(1, minutes) * 60
      const endsAt = Date.now() + total * 1000
      patch({ sleepRemainingSec: total, sleepLabel: `${minutes} min` })
      sleepTimerRef.current = window.setInterval(() => {
        const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
        patch({ sleepRemainingSec: left })
        if (left <= 0) {
          clearSleep()
          coreRef.current?.pause()
          busRef.current.emit('sleep', { reason: 'timer' })
        }
      }, 1000)
    },
    [clearSleep, patch],
  )

  // End-of-video sleep
  useEffect(() => {
    if (!ui.ended || !sleepEndModeRef.current) return
    clearSleep()
    coreRef.current?.pause()
    busRef.current.emit('sleep', { reason: 'end' })
  }, [ui.ended, clearSleep])

  const notifyShare = useCallback(
    (url: string) => {
      busRef.current.emit('share', { url })
    },
    [],
  )

  const setOfflineUi = useCallback(
    (offlineStatus: PlayerUiState['offlineStatus'], offlineProgress: number) => {
      patch({ offlineStatus, offlineProgress })
      busRef.current.emit('offline', { status: offlineStatus, progress: offlineProgress })
    },
    [patch],
  )

  const setCastingUi = useCallback(
    (casting: boolean) => {
      patch({ casting, castAvailable: true })
      busRef.current.emit('cast', { casting })
    },
    [patch],
  )

  const jumpToLive = useCallback(() => {
    coreRef.current?.quality.jumpToLive()
  }, [])

  const skipAd = useCallback(async () => {
    if (!adsRef.current.isPlayingAd() || !adCanSkipRef.current) return
    fireTrackingUrls(activeCreativeRef.current?.tracking?.skip)
    busRef.current.emit('adskip')
    const result = adsRef.current.skipCurrentAd()
    const core = coreRef.current
    if (!core) return
    if (result.next) {
      activeCreativeRef.current = result.next.creative
      startSkipCountdown(result.next.creative.skipAfterSeconds)
      patch({ adTitle: result.next.creative.title ?? 'Advertisement' })
      await core.loadAd(result.next.creative.src)
      return
    }
    await finishAdAndResume(result.resumeContentTime)
  }, [finishAdAndResume, patch, startSkipCountdown])

  const openAdClickThrough = useCallback(() => {
    const active = adsRef.current.getActive()
    if (active?.creative.clickThroughUrl) {
      window.open(active.creative.clickThroughUrl, '_blank', 'noopener,noreferrer')
    }
  }, [])

  const replay = useCallback(async () => {
    patch({ ended: false })
    seek(0)
    await requestPlay()
  }, [patch, requestPlay, seek])

  const on = useCallback((event: PlayerEventName | string, fn: (p?: unknown) => void) => {
    return busRef.current.on(event, fn)
  }, [])

  const off = useCallback((event: PlayerEventName | string, fn: (p?: unknown) => void) => {
    busRef.current.off(event, fn)
  }, [])

  return useMemo(
    () => ({
      videoRef,
      containerRef,
      ui,
      config,
      requestPlay,
      togglePlay,
      pause,
      seek,
      seekBy,
      setVolume,
      volumeBy,
      toggleMute,
      setPlaybackRate,
      setQuality,
      setAudioTrack,
      toggleFullscreen,
      togglePip,
      toggleTheater,
      setMiniPlayer,
      toggleCaptions,
      setCaptionStyle,
      setSettingsPanel,
      toggleInfo,
      toggleHotkeys,
      setControlsVisible,
      setBrightness,
      setSleepTimer,
      clearSleep,
      notifyShare,
      setOfflineUi,
      setCastingUi,
      jumpToLive,
      skipAd,
      openAdClickThrough,
      acceptResume,
      dismissResume,
      retry,
      replay,
      on,
      off,
      getHeatmap: () => busRef.current.getHeatmap(),
    }),
    [
      ui,
      config,
      requestPlay,
      togglePlay,
      pause,
      seek,
      seekBy,
      setVolume,
      volumeBy,
      toggleMute,
      setPlaybackRate,
      setQuality,
      setAudioTrack,
      toggleFullscreen,
      togglePip,
      toggleTheater,
      setMiniPlayer,
      toggleCaptions,
      setCaptionStyle,
      setSettingsPanel,
      toggleInfo,
      toggleHotkeys,
      setControlsVisible,
      setBrightness,
      setSleepTimer,
      clearSleep,
      notifyShare,
      setOfflineUi,
      setCastingUi,
      jumpToLive,
      skipAd,
      openAdClickThrough,
      acceptResume,
      dismissResume,
      retry,
      replay,
      on,
      off,
    ],
  )
}

export type PlayerApi = ReturnType<typeof usePlayer>
